import React from 'react';
import { Typography } from '@mui/material';

import useTokenReplacement from '../hooks/ui/useTokenReplacement';
import { chartLegend } from '../config/settings';

/**
 * TooltipContent - Formatted content component for chart tooltips
 * Complete migration from front/src/components/TooltipContent.js
 *
 * Renders structured tooltip content for climate indicator charts,
 * including title, reference values, and projections (min/mean/max).
 * Values received are already scaled for Y-axis, display formatting is applied here.
 *
 * @param {Object} props - Tooltip content props
 * @param {string} props.title - Indicator title
 * @param {string} [props._indicatorKey] - Indicator key (deprecated, kept for backward compatibility)
 * @param {Function} props.indicatorValueForDisplay - Display formatting function
 * @param {Function} props.indicatorTooltipTitle - Custom title formatting function
 * @param {string} props.seasonLabel - Season label for display
 * @param {number|null} props.reference - Reference period value (scaled)
 * @param {number|null} props.min - Minimum projection value (scaled)
 * @param {number|null} props.mean - Mean projection value (scaled)
 * @param {number|null} props.max - Maximum projection value (scaled)
 * @returns {React.ReactElement} Formatted tooltip content with Typography components
 *
 * @example
 * <TooltipContent
 *   title="Température moyenne"
 *   indicatorValueForDisplay={(val) => `${val}°C`}
 *   indicatorTooltipTitle={(title, season) => `${title} - ${season}`}
 *   seasonLabel="Printemps"
 *   reference={12.5}
 *   min={13.1}
 *   mean={14.2}
 *   max={15.3}
 * />
 */
const TooltipContent = ({
  title,
  _indicatorKey, // Deprecated: kept for backward compatibility
  indicatorValueForDisplay,
  indicatorTooltipTitle,
  seasonLabel,
  reference,
  min,
  mean,
  max,
}) => {
  const replaceTokens = useTokenReplacement();
  const customTitle = indicatorTooltipTitle(title, seasonLabel);

  return (
    <>
      {(title !== null) && (
        <Typography variant="body1">{customTitle}</Typography>
      )}
      {(reference !== null) && (
        <Typography color="inherit">
          {`- ${replaceTokens(chartLegend.legendItems[0].label)}: ${
            indicatorValueForDisplay(reference)
          }`}
        </Typography>
      )}
      {(max !== null) && (
        <Typography color="inherit">
          {`- ${replaceTokens(chartLegend.legendItems[1].label)}: ${
            indicatorValueForDisplay(max)
          }`}
        </Typography>
      )}
      {(mean !== null) && (
        <Typography color="inherit">
          {`- ${replaceTokens(chartLegend.legendItems[2].label)}: ${
            indicatorValueForDisplay(mean)
          }`}
        </Typography>
      )}
      {(min !== null) && (
        <Typography color="inherit">
          {`- ${replaceTokens(chartLegend.legendItems[3].label)}: ${
            indicatorValueForDisplay(min)
          }`}
        </Typography>
      )}
    </>
  );
};

TooltipContent.displayName = 'TooltipContent';

export default React.memo(TooltipContent);
